import React from 'react';
import { Table } from 'react-bootstrap';

import './_ContentSourcesList.scss';

import type { ContentSourceDetailsFragment } from '~/graphqlTypes/ContentSourceDetailsFragment';
import { ContentSourceLink } from '~/components/ContentSourceLink/ContentSourceLink';
import { pluralize } from '~/util/strings';
import { BEM } from '@feedfm/feed-util';

export interface ContentSourcesRowProps {
  source: ContentSourceDetailsFragment;
  bem: BEM;
}

export const _ContentSourcesRow: React.FC<ContentSourcesRowProps> = ( { bem: parentBem, source } ) => {
  const bem = parentBem.el( 'row-cell' );

  return (
    <React.Fragment key={ source.id }>
      <tr>
        <td className={ BEM.$( bem.mod( 'name' ) ) }>
          <ContentSourceLink reference={ { model: source } } />
          <span className={ BEM.$( bem.el( 'source-id' ) ) }>(#{ source.id })</span>
          <br />
          <div className={ BEM.$( bem.el( 'ddex-party-id' ) ) }>{ source.ddexPartyId && <>DDEX: { source.ddexPartyId }</> }</div>
        </td>
        <td className={ BEM.$( bem.mod( 'rights' ) ) }>
          <div className={ BEM.$( bem.el( 'default-rights', { none: source.defaultRights.length === 0 } ) ) }>
            { source.defaultRights.length === 0 && '-' }
            { source.defaultRights.length > 0 &&
              pluralize( source.defaultRights.length, 'default right', 'default rights' ) }
          </div>
          <div className={ BEM.$( bem.el( 'rights-mappings', { none: source.rightsMappings.length === 0 } ) ) }>
            { source.rightsMappings.length === 0 && '-' }
            { source.rightsMappings.length > 0 &&
              pluralize( source.rightsMappings.length, 'rights mapping', 'rights mappings' ) }
          </div>
        </td>
        <td className={ BEM.$( bem.mod( 'price_per_play' ) ) }>
          { source.pricePerPlay === 0 && (
            <>
              <span className="text-muted">-</span>
            </>
          ) }

          { source.pricePerPlay > 0 && `$${source.pricePerPlay.toFixed( 5 )}` }
        </td>
      </tr>
    </React.Fragment>
  );
};

_ContentSourcesRow.displayName = '_ContentSourcesRow';

export interface ContentSourcesListProps {
  className?: string;
  sources: ContentSourceDetailsFragment[];
}

export const _ContentSourcesList: React.FC<ContentSourcesListProps> = ( { sources } ) => {
  const bem = new BEM( 'ops-content-sources-list' );

  return (
    <div>
      <Table size="sm" striped bordered hover className={ BEM.$( bem ) }>
        <thead>
          <tr>
            <th scope="col" className={ BEM.$( bem.el( 'cell' ) ) }>
              Name
            </th>
            <th scope="col" className={ BEM.$( bem.el( 'cell' ) ) }>
              Rights configuration
            </th>
            <th scope="col" className={ BEM.$( bem.el( 'cell' ) ) }>
              Price per play (USD)
            </th>
          </tr>
        </thead>
        <tbody>
          { sources.map( ( source, index ) => (
            <_ContentSourcesRow key={ index } bem={ bem } source={ source } />
          ) ) }
        </tbody>
      </Table>
    </div>
  );
};

_ContentSourcesList.displayName = '_ContentSourcesList';
